import { Injectable } from '@angular/core';
import { SymbolType } from '../../../../../domain/symbol.domain';
import { TradingAccount } from '../../../../../domain/account.domain';
import { TimezoneService } from '../../../../../services/date/timezone.service';
import { DateService } from '../../../../../services/date/date.service';
import {
  OptionExtendedTerms,
  OptionOrderAction,
  OrderActionParam,
  OrderDateType,
  OrderItem,
  PriceType,
  Strategy
} from '../../../domain';
import {
  OptionFormModel,
  OptionSymbolUrlParam,
  OrderFormParameters,
  OrderStateParameters
} from '../../domain/order-entry.domain';

import { OrderEntryUtilityService } from '../order-entry-utility.service';
import { CreateOrderFormStocksOptionsBaseFormService } from './create-order-stocks-options-base-form.service';

@Injectable()
export class CreateOrderOptionFormService extends CreateOrderFormStocksOptionsBaseFormService<OptionFormModel> {

  constructor(protected timezoneService: TimezoneService) {
    super();
  }

  /**
   * Initialize an object that represents the state of UI across multiple steps
   */
  initializeViewModel(
    selectedAccount?: TradingAccount,
    orderFormParameters?: OrderFormParameters,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _strategy: Strategy = Strategy.SPL,
    index: number = 0
  ): OptionFormModel {
    this.initFormModel();
    if (orderFormParameters?.orderStateParameters?.orderKey === undefined) {
      this.initFromParameters(selectedAccount, orderFormParameters);
    } else {
      // its copy order, load object
      const orderDetails = orderFormParameters.orderDetails;
      const orderItem = orderDetails.orderTerms.order[index]; // have complex-service.initialize handle this properly

      this.processOpenTriggeredOrder(orderItem);
      this.copyOrder(orderItem);
      this.setOrderSymbol({
        symbol: orderItem.security.displayTicker,
        market: orderItem.security.market,
        optionNonStandard: false
      });
      this.setOrderFormPriceType(orderItem.extendedTerms as OptionExtendedTerms);
    }
    return Object.assign({}, this.temp); // TODO double check
  }

  copyOrder(orderItem: OrderItem): void {
    this.setCopyOrderAction(orderItem);
    this.temp.orderQuantity = orderItem.quantity;

    const extendedTerms = orderItem.extendedTerms as OptionExtendedTerms;
    this.setOrderFormPriceType(extendedTerms);
    this.setOrderFormGoodTil(extendedTerms);
    this.setOrderShareholderStatus(orderItem.clientInfo);
    this.setOrderAllOrNone(extendedTerms);
  }

  setCopyOrderAction(orderItem: OrderItem): void {
    const orderAction = orderItem.extendedTerms.orderAction as OptionOrderAction;
    if (orderAction !== undefined) {
      this.temp.orderAction = orderAction;
    }
  }

  setOrderSymbol(parameters: Pick<OrderStateParameters, 'symbol' | 'market' | 'optionNonStandard'>): void {
    const optionValues = OrderEntryUtilityService.parseOptionUrlParams(parameters.symbol);
    const symbolString = optionValues.underlyingSymbol;
    this.setOptionQuoteParams(optionValues, parameters.optionNonStandard);
    this.setSymbol({
      symbol: symbolString,
      market: parameters.market,
      symbolType: SymbolType.OPTION
    });
  }

  setOptionQuoteParams(param: OptionSymbolUrlParam, optionNonStandard: boolean): void {
    this.temp.optionQuoteParams = {
      expiryDate: param.expiryDate,
      strikePrice: param.strikePrice,
      optionType: param.optionType,
      underlyingCompany: param.underlyingSymbol,
      displayTicker: param.underlyingSymbol,
      nonStandard: optionNonStandard
    };
    this.temp.nonStandard = optionNonStandard;
  }

  setOrderFormPriceType(extendedTerms: OptionExtendedTerms): void {
    this.temp.orderPrice = extendedTerms.orderType;
    this.setPriceAmount(extendedTerms);
  }

  setOrderFormAction(parameters: Pick<OrderStateParameters, 'action' | 'quantity'>): void {
    if (parameters.action !== undefined) {
      this.temp.orderAction = OrderEntryUtilityService
        .mapOrderActionParam(parameters.action as OrderActionParam, parameters.quantity) as OptionOrderAction;
    }
  }

  getTodaysDate(): Date {
    return DateService.stripTime(this.timezoneService.getCurrentDateInServerTimezone());
  }

  isFutureOptionDate(expiryDate: Date): boolean {
    const today = this.getTodaysDate();
    return DateService.getUTCTimeDifferenceInMilliseconds(today, expiryDate) >= 0;
  }

  protected setParameters(parameters: OrderStateParameters): void {
    // watch out: bool flags follow js boolean definition from url string
    // ie flag="false" or "0" will come here as true, since any non-empty string is true
    if (parameters.optionNonStandard !== undefined) {
      const isNonStandard = String(parameters.optionNonStandard) === 'true';
      this.temp.nonStandard = isNonStandard;
      this.temp.optionQuoteParams.nonStandard = isNonStandard;
    }
  }

  private initFormModel(): void {
    this.temp = {
      orderAction: undefined,
      orderPrice: PriceType.MARKET,
      shareholderStatus: undefined,
      orderGoodTil: OrderDateType.DAY,
      orderGoodTilDate: undefined,
      orderAllOrNone: false,
      orderQuantity: undefined,
      selectedSymbol: undefined,
      orderPriceAmount: {
        priceLimit: undefined,
        priceTrigger: undefined,
        deltaLimit: undefined,
        deltaTrigger: undefined,
        deltaPercent: undefined
      },
      nonStandard: false,
      optionQuoteParams: undefined
    };
  }
}

